/**
 * alphaRsi — Wilder's RSI with a smoothed signal line + zone classifier.
 *
 * JavaScript port of the user's NinjaTrader 8 indicator
 * `optimized_rsi_alphawolf.cs` (file: `rsi-optimized.cs`) located at
 * `E:\AlphaWolfTrader\Code Construction\Current Running Code\Indicators\AlphaWolfTrader\`.
 * Used on every Chris-published chart, on every timeframe (D/4H/1H/15M/5M).
 *
 * Two outputs:
 *   - `rsi`    — Wilder's 14-period RSI on close[i] − close[i−1].
 *   - `signal` — EMA-style smoothed line over RSI, with smoothing constant
 *                k = 2 / (1 + smooth). The original NT indicator labels this
 *                plot "Avg".
 *
 * Plus three derived state classifiers consumed by the Top-Down state vector
 * (see `docs/research/Coach_TopDown_Analysis.md` §2):
 *   - `classifyRsiZone(value)` → 'oversold' | 'neutral' | 'overbought'
 *   - `detectRsiSlope(series, lookback)` → 'rising' | 'flat' | 'falling'
 *   - `detectSignalCross(rsiSeries, signalSeries, lookback)` → 'bullish' | 'bearish' | null
 *
 * Default thresholds mirror the indicator's defaults: OS=30 / Mid=50 / OB=70.
 *
 * Pure function. No side effects. NaN-resilient: invalid bars produce null
 * points and the running averages pause rather than poisoning the series.
 */

export const DEFAULT_OPTS = Object.freeze({
  period: 14,         // Wilder's RSI lookback
  smooth: 3,          // signal-line smoothing
  oversold: 30,
  midpoint: 50,
  overbought: 70,
  slopeLookbackBars: 3,    // bars over which to measure RSI slope
  signalCrossLookback: 3,  // bars over which signal-line cross counts as "recent"
  flatTolerance: 1.0,      // |Δ RSI| <= this counts as 'flat' over slopeLookbackBars
});

/**
 * Simple moving average of a numeric array slice — used only for the RSI
 * seed (first Wilder-smoothing input).
 */
const sma = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  let sum = 0;
  let n = 0;
  for (const v of values) {
    if (Number.isFinite(v)) {
      sum += v;
      n++;
    }
  }
  return n > 0 ? sum / n : null;
};

/**
 * Compute AlphaRSI: 14-period Wilder RSI + a separately-smoothed signal line.
 *
 * @param {Array<{time:number, close:number}>} bars  Sorted ascending by time.
 * @param {Object} [opts]  Override defaults — `period`, `smooth`.
 * @returns {{rsi: Array<{time:number, value:number|null}>,
 *            signal: Array<{time:number, value:number|null}>}}
 *          Both series have length === bars.length. Warm-up bars hold null.
 */
export const computeAlphaRsi = (bars, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) {
    return { rsi: [], signal: [] };
  }

  const period = Math.floor(Number(opts.period ?? DEFAULT_OPTS.period));
  const smooth = Math.floor(Number(opts.smooth ?? DEFAULT_OPTS.smooth));
  if (!Number.isFinite(period) || period < 2) return { rsi: [], signal: [] };
  if (!Number.isFinite(smooth) || smooth < 1) return { rsi: [], signal: [] };

  // Signal-line constants — same shape as the NT C# indicator.
  const k = 2 / (1 + smooth);
  const k1 = 1 - k;

  const rsi = new Array(bars.length);
  const signal = new Array(bars.length);

  // First bar has no preceding close → RSI undefined for index 0.
  rsi[0] = { time: Number(bars[0]?.time), value: null };
  signal[0] = { time: Number(bars[0]?.time), value: null };

  // Collect up[]/down[] starting at index 1; seed Wilder averages at index `period`.
  const upBuf = []; // recent up values, kept short until seeding completes
  const downBuf = [];
  let avgUp = null;
  let avgDown = null;
  let lastSignal = null;

  for (let i = 1; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    const close = Number(bars[i]?.close);
    const prevClose = Number(bars[i - 1]?.close);

    if (!Number.isFinite(close) || !Number.isFinite(prevClose)) {
      // Pause the running averages on a bad bar so a single NaN doesn't
      // corrupt the rest of the series.
      rsi[i] = { time: t, value: null };
      signal[i] = { time: t, value: null };
      continue;
    }

    const change = close - prevClose;
    const up = change > 0 ? change : 0;
    const down = change < 0 ? -change : 0;

    if (avgUp === null) {
      // Still seeding the Wilder smoother.
      upBuf.push(up);
      downBuf.push(down);
      if (upBuf.length === period) {
        avgUp = sma(upBuf);
        avgDown = sma(downBuf);
      } else {
        rsi[i] = { time: t, value: null };
        signal[i] = { time: t, value: null };
        continue;
      }
    } else {
      // Wilder smoothing: avg = (prev * (period-1) + current) / period
      avgUp = (avgUp * (period - 1) + up) / period;
      avgDown = (avgDown * (period - 1) + down) / period;
    }

    let rsiValue;
    if (avgDown === 0) {
      rsiValue = 100;
    } else {
      const rs = avgUp / avgDown;
      rsiValue = 100 - 100 / (1 + rs);
    }
    rsi[i] = { time: t, value: rsiValue };

    // Signal line: EMA-style smoothing over RSI.
    if (lastSignal === null) {
      lastSignal = rsiValue;
    } else {
      lastSignal = k * rsiValue + k1 * lastSignal;
    }
    signal[i] = { time: t, value: lastSignal };
  }

  return { rsi, signal };
};

/**
 * Classify an RSI value into its zone using the provided thresholds.
 * `null` / non-finite → null (caller decides what to render).
 *
 * @param {number|null} value
 * @param {Object} [opts]  Override `oversold` / `overbought`.
 * @returns {'oversold' | 'neutral' | 'overbought' | null}
 */
export const classifyRsiZone = (value, opts = {}) => {
  if (!Number.isFinite(value)) return null;
  const oversold = Number(opts.oversold ?? DEFAULT_OPTS.oversold);
  const overbought = Number(opts.overbought ?? DEFAULT_OPTS.overbought);
  if (value <= oversold) return 'oversold';
  if (value >= overbought) return 'overbought';
  return 'neutral';
};

/**
 * Classify the slope of an RSI series over the last `lookbackBars` (default 3).
 * Slope = (RSI[end] − RSI[end − lookback]). Compared to `flatTolerance` to
 * give a 'flat' band; outside that band is 'rising' or 'falling'.
 *
 * Returns null when fewer than 2 valid points are available.
 *
 * @param {Array<{time:number, value:number|null}>} series
 * @param {Object} [opts]  `slopeLookbackBars`, `flatTolerance`.
 * @returns {'rising' | 'flat' | 'falling' | null}
 */
export const detectRsiSlope = (series, opts = {}) => {
  if (!Array.isArray(series) || series.length < 2) return null;
  const lookback = Math.max(1, Math.floor(Number(opts.slopeLookbackBars ?? DEFAULT_OPTS.slopeLookbackBars)));
  const tolerance = Number(opts.flatTolerance ?? DEFAULT_OPTS.flatTolerance);

  // Walk backwards from the end to find the most recent valid value and one
  // `lookback` bars before it. Skipping nulls lets noisy bars not break the
  // slope read.
  let endIdx = -1;
  for (let i = series.length - 1; i >= 0; i--) {
    if (Number.isFinite(series[i]?.value)) {
      endIdx = i;
      break;
    }
  }
  if (endIdx < 0) return null;
  const targetIdx = endIdx - lookback;
  if (targetIdx < 0) return null;

  // Find the nearest valid value at or before targetIdx (also tolerant of gaps).
  let startIdx = -1;
  for (let i = targetIdx; i >= 0; i--) {
    if (Number.isFinite(series[i]?.value)) {
      startIdx = i;
      break;
    }
  }
  if (startIdx < 0) return null;

  const delta = series[endIdx].value - series[startIdx].value;
  if (Math.abs(delta) <= tolerance) return 'flat';
  return delta > 0 ? 'rising' : 'falling';
};

/**
 * Detect whether the RSI crossed its signal line within the last
 * `signalCrossLookback` bars. Returns the direction of the most recent
 * cross or null if no cross is recent.
 *
 * Logic: a cross occurs when (rsi[i] − signal[i]) flips sign vs
 * (rsi[i−1] − signal[i−1]). Bullish = RSI crosses UP through signal.
 *
 * @param {Array<{time:number, value:number|null}>} rsiSeries
 * @param {Array<{time:number, value:number|null}>} signalSeries
 * @param {Object} [opts]  `signalCrossLookback`.
 * @returns {'bullish' | 'bearish' | null}
 */
export const detectSignalCross = (rsiSeries, signalSeries, opts = {}) => {
  if (!Array.isArray(rsiSeries) || !Array.isArray(signalSeries)) return null;
  if (rsiSeries.length !== signalSeries.length) return null;
  const lookback = Math.max(1, Math.floor(Number(opts.signalCrossLookback ?? DEFAULT_OPTS.signalCrossLookback)));

  // Scan backwards through the lookback window; report the MOST RECENT cross.
  const start = Math.max(1, rsiSeries.length - lookback);
  for (let i = rsiSeries.length - 1; i >= start; i--) {
    const rsiNow = rsiSeries[i]?.value;
    const sigNow = signalSeries[i]?.value;
    const rsiPrev = rsiSeries[i - 1]?.value;
    const sigPrev = signalSeries[i - 1]?.value;
    if (![rsiNow, sigNow, rsiPrev, sigPrev].every(Number.isFinite)) continue;

    const diffNow = rsiNow - sigNow;
    const diffPrev = rsiPrev - sigPrev;
    if (diffPrev <= 0 && diffNow > 0) return 'bullish';
    if (diffPrev >= 0 && diffNow < 0) return 'bearish';
  }
  return null;
};

/**
 * Look up the RSI (or signal) value at or just before a given timestamp.
 * Mirrors `emaAtTime` in shape.
 *
 * @param {Array<{time:number, value:number|null}>} series
 * @param {number} targetTime  Unix seconds.
 * @returns {number|null}
 */
export const alphaRsiAtTime = (series, targetTime) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  if (!Number.isFinite(targetTime)) return null;
  let lastValue = null;
  for (const point of series) {
    if (!point) continue;
    if (point.time > targetTime) break;
    if (Number.isFinite(point.value)) lastValue = point.value;
  }
  return lastValue;
};

/**
 * Snapshot the full AlphaRSI state at a target timestamp. Returns the
 * exact shape consumed by the Top-Down per-TF state vector:
 *
 *   {
 *     value: number,            // latest RSI at or before targetTime
 *     signal: number,           // latest signal-line value
 *     zone: 'oversold' | 'neutral' | 'overbought',
 *     slope: 'rising' | 'flat' | 'falling',
 *     signalCross: 'bullish' | 'bearish' | null  // within last `signalCrossLookback` bars
 *   }
 *
 * Any field that can't be determined is null. `value === null` implies the
 * series hasn't warmed up at the target time.
 *
 * @param {{rsi: Array, signal: Array}} computed  Output of `computeAlphaRsi`.
 * @param {number} targetTime  Unix seconds.
 * @param {Object} [opts]  Forwarded to classifiers.
 */
export const alphaRsiStateAtTime = (computed, targetTime, opts = {}) => {
  if (!computed || !Array.isArray(computed.rsi) || !Array.isArray(computed.signal)) {
    return { value: null, signal: null, zone: null, slope: null, signalCross: null };
  }
  // Truncate series to up-to-targetTime so slope/cross detection respects the
  // "as of" time, not "right now if the bars ran past targetTime."
  const cutoff = Number(targetTime);
  let cut = computed.rsi.length;
  if (Number.isFinite(cutoff)) {
    for (let i = 0; i < computed.rsi.length; i++) {
      if (Number(computed.rsi[i]?.time) > cutoff) {
        cut = i;
        break;
      }
    }
  }
  const rsiUpTo = computed.rsi.slice(0, cut);
  const sigUpTo = computed.signal.slice(0, cut);

  const value = alphaRsiAtTime(rsiUpTo, cutoff);
  const signal = alphaRsiAtTime(sigUpTo, cutoff);
  return {
    value,
    signal,
    zone: classifyRsiZone(value, opts),
    slope: detectRsiSlope(rsiUpTo, opts),
    signalCross: detectSignalCross(rsiUpTo, sigUpTo, opts),
  };
};
